import React, { useCallback, useState } from "react";
import { Button, Col, FormGroup, Input, Label, Row, Spinner } from "reactstrap";

import { useToast } from "hooks/toast";
import { adicionarFilial, removerFilial } from "services/onboardingApi";
import { digitsOnly, formatDocument, isValidCnpj } from "utils/formatDoc";

// Filiais/plantas (remetentes) — PO1-2428. O BrasilAPI preenche cada CNPJ,
// mas NÃO lista as filiais sozinho: o operador inclui só as que embarcam.
export default function FiliaisStep({ idEmbarcador, filiais, onChanged }) {
  const { addToast } = useToast();
  const [cnpj, setCnpj] = useState("");
  const [adicionando, setAdicionando] = useState(false);
  const [removendo, setRemovendo] = useState(null);

  const handleAdicionar = useCallback(async () => {
    if (!isValidCnpj(cnpj)) {
      addToast({
        place: "tr",
        type: "danger",
        title: "CNPJ inválido",
        icon: "tim-icons icon-alert-circle-exc",
      });
      return;
    }
    setAdicionando(true);
    try {
      await adicionarFilial(idEmbarcador, { cnpj: digitsOnly(cnpj) });
      setCnpj("");
      addToast({
        place: "tr",
        type: "success",
        title: "Filial adicionada",
        description: "Dados preenchidos pela BrasilAPI.",
        icon: "tim-icons icon-check-2",
      });
      onChanged();
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.error) ||
        err.message;
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro ao adicionar filial",
        description: msg,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setAdicionando(false);
    }
  }, [cnpj, idEmbarcador, addToast, onChanged]);

  const handleRemover = useCallback(
    async (filial) => {
      setRemovendo(filial.id);
      try {
        await removerFilial(idEmbarcador, filial.id);
        addToast({
          place: "tr",
          type: "success",
          title: "Filial removida",
          icon: "tim-icons icon-check-2",
        });
        onChanged();
      } catch (err) {
        const msg =
          (err.response && err.response.data && err.response.data.error) ||
          err.message;
        addToast({
          place: "tr",
          type: "danger",
          title: "Erro ao remover filial",
          description: msg,
          icon: "tim-icons icon-alert-circle-exc",
        });
      } finally {
        setRemovendo(null);
      }
    },
    [idEmbarcador, addToast, onChanged]
  );

  return (
    <div>
      {(filiais || []).map((filial) => (
        <div className="onb-filial" key={filial.id}>
          <div>
            <div className="nm">{filial.razaoSocial}</div>
            <div className="ci">{filial.fantasia}</div>
          </div>
          <div className="d-flex align-items-center" style={{ gap: 12 }}>
            <span className="ci">{formatDocument(filial.cnpjCpf)}</span>
            {filial.temToken ? (
              <span className="onb-badge done" title="Credencial emitida">
                credenciada
              </span>
            ) : (
              <Button
                close
                title="remover"
                disabled={removendo === filial.id}
                onClick={() => handleRemover(filial)}
              />
            )}
          </div>
        </div>
      ))}

      <Row className="align-items-end">
        <Col md="5">
          <FormGroup>
            <Label>CNPJ da filial</Label>
            <Input
              value={formatDocument(cnpj)}
              onChange={(e) => setCnpj(e.target.value)}
              placeholder="00.000.000/0000-00"
            />
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <Button
              color="primary"
              onClick={handleAdicionar}
              disabled={adicionando}
            >
              {adicionando ? <Spinner size="sm" /> : "+ Adicionar filial"}
            </Button>
          </FormGroup>
        </Col>
      </Row>

      <div className="onb-info">
        <span role="img" aria-label="informação">ℹ️</span>
        <span>
          O BrasilAPI preenche cada CNPJ, mas <b>não lista as filiais sozinho</b>
          . Inclua só as plantas que realmente embarcam.
        </span>
      </div>
    </div>
  );
}
